/**
 * Global guard: stop a stray backdrop click from closing a modal when the
 * user was really just selecting text.
 *
 * A native `click` is dispatched on the *nearest common ancestor* of the
 * mousedown and mouseup targets. So when you click-drag to select text in a
 * modal field (mousedown on the `<input>`) and release over the dimmed
 * backdrop (mouseup on the backdrop), the resulting click lands on the
 * backdrop — and every modal's `onClick={onClose}` fires, nuking the popup
 * mid-edit. Agents hit this constantly when dragging across a file name or
 * title.
 *
 * Fix: remember where each press *started*. If a click reaches a backdrop
 * but the press began on a descendant (i.e. inside the modal), it's the tail
 * of a selection drag, not a dismiss — swallow it in the capture phase so
 * React's delegated handler never runs. A genuine press-and-release on the
 * backdrop still dismisses normally.
 *
 * What counts as a backdrop:
 *  - anything with the `.modal-backdrop` class (the styled modal system), or
 *  - any fixed-position element covering the full viewport — the ad-hoc
 *    `position: fixed; inset: 0` overlays used by SaveToOneDriveModal,
 *    PickFromFilesModal, the mailings/comms/CMA dialogs, etc. Those never
 *    got the class, so the original class-only guard missed them.
 *
 * Why one document-level listener covers everything: the capture phase starts
 * at `document`, so this runs before any other listener in the path —
 * including React's (attached on the root container) and any inside a portal.
 * `stopPropagation()` here halts the event at the very first step, so it never
 * reaches React regardless of where React listens. That makes this work for
 * inline modals and `createPortal`-to-`<body>` modals alike, with no change
 * to the 30-plus modal components.
 */
function isBackdrop(el: Element): boolean {
  if (el.classList.contains('modal-backdrop')) return true;
  // Ad-hoc overlay: a fixed element spanning the whole viewport. Compare
  // against documentElement.clientWidth/Height, not window.innerWidth/Height —
  // a fixed `inset: 0` element stops at the scrollbar, which innerWidth
  // includes. The -1 tolerance absorbs sub-pixel rounding on zoomed displays.
  const rect = el.getBoundingClientRect();
  if (
    rect.left > 1 ||
    rect.top > 1 ||
    rect.width < document.documentElement.clientWidth - 1 ||
    rect.height < document.documentElement.clientHeight - 1
  ) {
    return false;
  }
  return getComputedStyle(el).position === 'fixed';
}

let pressTarget: EventTarget | null = null;

export function installModalBackdropGuard(): void {
  document.addEventListener(
    'mousedown',
    (e) => {
      pressTarget = e.target;
    },
    true,
  );
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      // Press began on this very element → a genuine backdrop click, let it
      // dismiss. Anything else that *lands* on a backdrop started elsewhere
      // (almost always a text-selection drag inside the modal) → kill it
      // before it reaches the modal's onClick.
      if (pressTarget !== target && isBackdrop(target)) {
        e.stopPropagation();
      }
    },
    true,
  );
}
